import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PageHeader from 'components/Layout/PageHeader'
import Card from 'components/Layout/Card'
import Button, { ButtonTheme } from 'components/Form/Button'
import Modal from 'components/Modal'
import ModalUpload from 'components/Modal/ModalUpload/ModalUpload'
import { CREATIONS_MAX_FILE_SIZE, CREATIONS_MAX_FILES } from 'constant'
import VariableUtils from 'utils/variable'
import {
  faCloudUploadAlt,
  faSpinner,
  faCheck,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { AppContext } from 'contexts/AppContext'
import { NotifierType } from 'components/Notifier'
import api from 'api'
import { ModalUploadFile } from 'components/Modal/ModalUpload'
import './AICreationUpload.scss'

const POLLING_INTERVAL_MS = 3000

type FileMediaType = 'image' | 'video' | 'other'

interface JobEntry {
  id: string
  fileName: string
  fileAiDescriptionId: string
  status: 'uploading' | 'processing' | 'finished' | 'error'
  result: Record<string, unknown> | null
  error: string | null
  previewUrl: string | null
  fileType: FileMediaType
}

const getFileMediaType = (file: File): FileMediaType => {
  if (file.type.startsWith('image/')) return 'image'
  if (file.type.startsWith('video/')) return 'video'
  return 'other'
}

type ResultTab = 'formatted' | 'json'

const formatKey = (key: string): string =>
  key
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]/g, ' ')
    .replace(/^\w/, c => c.toUpperCase())
    .trim()

const renderFormattedValue = (value: unknown, depth = 0): React.ReactNode => {
  if (value === null || value === undefined) {
    return <span className='AICreationUpload__formatted-empty'>—</span>
  }

  if (typeof value === 'boolean') {
    return <span className='AICreationUpload__formatted-bool'>{value ? '✓ Yes' : '✗ No'}</span>
  }

  if (typeof value === 'number' || typeof value === 'string') {
    return <span className='AICreationUpload__formatted-text'>{String(value)}</span>
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className='AICreationUpload__formatted-empty'>—</span>
    }

    // Array of primitives
    if (value.every(v => typeof v !== 'object' || v === null)) {
      return (
        <ul className='AICreationUpload__formatted-list'>
          {value.map((item, i) => (
            <li
              key={i}
              className='AICreationUpload__formatted-list-item'
            >
              {String(item)}
            </li>
          ))}
        </ul>
      )
    }

    // Array of objects
    return (
      <div className='AICreationUpload__formatted-array'>
        {value.map((item, i) => (
          <div
            key={i}
            className='AICreationUpload__formatted-array-item'
          >
            <div className='AICreationUpload__formatted-array-badge'>#{i + 1}</div>
            {renderFormattedValue(item, depth + 1)}
          </div>
        ))}
      </div>
    )
  }

  if (typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
    return (
      <div
        className={`AICreationUpload__formatted-object ${
          depth > 0 ? 'AICreationUpload__formatted-object--nested' : ''
        }`}
      >
        {entries.map(([key, val]) => {
          const isComplex = typeof val === 'object' && val !== null
          return (
            <div
              key={key}
              className='AICreationUpload__formatted-row'
            >
              <div
                className={`AICreationUpload__formatted-key ${
                  isComplex ? 'AICreationUpload__formatted-key--section' : ''
                }`}
              >
                {formatKey(key)}
              </div>
              <div className='AICreationUpload__formatted-value'>
                {renderFormattedValue(val, depth + 1)}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  return <span>{String(value)}</span>
}

const AICreationUpload: React.FC = () => {
  const { t } = useTranslation()
  const { addNotification } = useContext(AppContext)
  const [isUploadModalOpen, setIsUploadModalOpen] = useState<boolean>(false)
  const [jobs, setJobs] = useState<JobEntry[]>([])
  const [resultTab, setResultTab] = useState<ResultTab>('formatted')
  const pollingTimers = useRef<Map<string, ReturnType<typeof setInterval>>>(new Map())

  // Cleanup polling and preview URLs on unmount
  useEffect(() => {
    return () => {
      pollingTimers.current.forEach(timer => clearInterval(timer))
      jobs.forEach(job => {
        if (job.previewUrl) URL.revokeObjectURL(job.previewUrl)
      })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const updateJob = (fileAiDescriptionId: string, updates: Partial<JobEntry>) => {
    setJobs(prev =>
      prev.map(job =>
        job.fileAiDescriptionId === fileAiDescriptionId ? { ...job, ...updates } : job
      )
    )
  }

  const startPolling = useCallback(
    (fileAiDescriptionId: string) => {
      const timer = setInterval(() => {
        api.aiDescription
          .getCreationAiDescriptionResult(fileAiDescriptionId)
          .then(res => {
            const { status, result } = res.data.creationAiDescriptionResult

            if (status === 'finished') {
              clearInterval(timer)
              pollingTimers.current.delete(fileAiDescriptionId)

              let parsedResult: Record<string, unknown> | null = null
              try {
                parsedResult = typeof result === 'string' ? JSON.parse(result) : result
              } catch {
                parsedResult = { raw: result }
              }

              updateJob(fileAiDescriptionId, { status: 'finished', result: parsedResult })
              addNotification(NotifierType.SUCCESS, t('aiCreationUpload.jobFinished'))
            } else if (status === 'error' || status === 'failed') {
              clearInterval(timer)
              pollingTimers.current.delete(fileAiDescriptionId)
              updateJob(fileAiDescriptionId, {
                status: 'error',
                error: result || t('aiCreationUpload.jobError'),
              })
              addNotification(NotifierType.ERROR, t('aiCreationUpload.jobError'))
            }
            // otherwise keep polling (status is e.g. 'processing', 'pending', etc.)
          })
          .catch(() => {
            clearInterval(timer)
            pollingTimers.current.delete(fileAiDescriptionId)
            updateJob(fileAiDescriptionId, {
              status: 'error',
              error: t('aiCreationUpload.pollingError'),
            })
          })
      }, POLLING_INTERVAL_MS)

      pollingTimers.current.set(fileAiDescriptionId, timer)
    },
    [addNotification, t]
  )

  const handleUpload = (files: ModalUploadFile[], setLoading: (loading: boolean) => void) => {
    if (files.length === 0) return

    setLoading(true)
    const file = files[0] // Upload first file

    // Clear previous jobs and revoke their preview URLs
    jobs.forEach(job => {
      if (job.previewUrl) URL.revokeObjectURL(job.previewUrl)
    })
    pollingTimers.current.forEach(timer => clearInterval(timer))
    pollingTimers.current.clear()
    setJobs([])

    const tempId = `temp-${Date.now()}`
    const fileType = getFileMediaType(file)
    const previewUrl = fileType !== 'other' ? URL.createObjectURL(file) : null

    const newJob: JobEntry = {
      id: tempId,
      fileName: file.name,
      fileAiDescriptionId: tempId,
      status: 'uploading',
      result: null,
      error: null,
      previewUrl,
      fileType,
    }

    setJobs(prev => [newJob, ...prev])
    setIsUploadModalOpen(false)

    console.log('createAiDescriptionJob uploading file:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: file.lastModified,
      isFile: file instanceof File,
      isBlob: file instanceof Blob,
    })

    api.aiDescription
      .createAiDescriptionJob(file)
      .then(res => {
        const data = res.data?.createAiDescriptionJob
        console.log('createAiDescriptionJob response:', res)
        if (res.errors) {
          console.error('createAiDescriptionJob GraphQL errors:', res.errors)
        }

        if (!data?.success || !data?.fileAiDescriptionId) {
          console.error('createAiDescriptionJob errors:', data?.errors)
          const errorMsg: string = data?.errors?.join(', ') || t('aiCreationUpload.uploadError')
          setJobs(prev =>
            prev.map(job =>
              job.id === tempId ? { ...job, status: 'error' as const, error: errorMsg } : job
            )
          )
          addNotification(NotifierType.ERROR, errorMsg)
          return
        }

        const fileAiDescriptionId = data.fileAiDescriptionId

        setJobs(prev =>
          prev.map(job =>
            job.id === tempId ? { ...job, fileAiDescriptionId, status: 'processing' } : job
          )
        )

        addNotification(NotifierType.SUCCESS, t('aiCreationUpload.uploadSuccess'))
        startPolling(fileAiDescriptionId)
      })
      .catch(err => {
        console.error('createAiDescriptionJob failed:', err)
        setJobs(prev =>
          prev.map(job =>
            job.id === tempId
              ? { ...job, status: 'error', error: t('aiCreationUpload.uploadError') }
              : job
          )
        )
        addNotification(NotifierType.ERROR, t('aiCreationUpload.uploadError'))
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const getStatusIcon = (status: JobEntry['status']) => {
    switch (status) {
      case 'uploading':
      case 'processing':
        return (
          <FontAwesomeIcon
            icon={faSpinner}
            spin
            className='AICreationUpload__status-icon--processing'
          />
        )
      case 'finished':
        return (
          <FontAwesomeIcon
            icon={faCheck}
            className='AICreationUpload__status-icon--finished'
          />
        )
      case 'error':
        return (
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            className='AICreationUpload__status-icon--error'
          />
        )
    }
  }

  const getStatusLabel = (status: JobEntry['status']): string => {
    switch (status) {
      case 'uploading':
        return t('aiCreationUpload.statusUploading')
      case 'processing':
        return t('aiCreationUpload.statusProcessing')
      case 'finished':
        return t('aiCreationUpload.statusFinished')
      case 'error':
        return t('aiCreationUpload.statusError')
    }
  }

  const DropHeader = (
    <div className='AICreationUpload__drop-header'>
      <div className='AICreationUpload__drop-info'>
        <FontAwesomeIcon
          icon={faCloudUploadAlt}
          className='AICreationUpload__drop-icon'
        />
        <div className='AICreationUpload__drop-title'>{t('aiCreationUpload.uploadCreation')}</div>
      </div>

      <div className='AICreationUpload__requirements'>
        <div className='AICreationUpload__accepted-formats'>
          {`${t('form.campaign.creations.acceptedFormats')}: `}
          <span className='AICreationUpload__bold'>mp4, jpg, jpeg, png, gif</span>
        </div>
        <div>
          {`${t('form.campaign.creations.maxFileSize')}: `}
          <span className='AICreationUpload__bold'>
            {VariableUtils.formatBytes(CREATIONS_MAX_FILE_SIZE)}
          </span>
        </div>
        <div>
          {`${t('form.campaign.creations.maxFiles')}: `}
          <span className='AICreationUpload__bold'>{CREATIONS_MAX_FILES}</span>
        </div>
      </div>
    </div>
  )

  return (
    <div className='AICreationUpload'>
      <PageHeader>{t('aiCreationUpload.title')}</PageHeader>

      <Card className='AICreationUpload__card'>
        <div className='AICreationUpload__content'>
          <p className='AICreationUpload__description'>{t('aiCreationUpload.description')}</p>

          <Button
            theme={ButtonTheme.BLUE}
            onClick={() => setIsUploadModalOpen(true)}
            className='AICreationUpload__upload-btn'
          >
            <FontAwesomeIcon icon={faCloudUploadAlt} />
            {t('aiCreationUpload.uploadButton')}
          </Button>

          {jobs.length > 0 && (
            <div className='AICreationUpload__jobs'>
              <h3>{t('aiCreationUpload.uploadedFiles')}</h3>

              {jobs.map(job => (
                <div
                  key={job.id}
                  className={`AICreationUpload__job AICreationUpload__job--${job.status}`}
                >
                  <div className='AICreationUpload__job-header'>
                    <div className='AICreationUpload__job-info'>
                      {getStatusIcon(job.status)}
                      <span className='AICreationUpload__job-name'>{job.fileName}</span>
                    </div>
                    <span
                      className={`AICreationUpload__job-status AICreationUpload__job-status--${job.status}`}
                    >
                      {getStatusLabel(job.status)}
                    </span>
                  </div>

                  {job.previewUrl && (
                    <div className='AICreationUpload__job-preview'>
                      {job.fileType === 'image' ? (
                        <img
                          src={job.previewUrl}
                          alt={job.fileName}
                          className='AICreationUpload__preview-media'
                        />
                      ) : (
                        <video
                          src={job.previewUrl}
                          controls
                          className='AICreationUpload__preview-media'
                        />
                      )}
                    </div>
                  )}

                  {job.status === 'error' && job.error && (
                    <div className='AICreationUpload__job-error'>{job.error}</div>
                  )}

                  {job.status === 'finished' && job.result && (
                    <div className='AICreationUpload__job-result'>
                      <div className='AICreationUpload__result-tabs'>
                        <button
                          className={`AICreationUpload__result-tab ${
                            resultTab === 'formatted' ? 'AICreationUpload__result-tab--active' : ''
                          }`}
                          onClick={() => setResultTab('formatted')}
                        >
                          {t('aiCreationUpload.resultFormatted')}
                        </button>
                        <button
                          className={`AICreationUpload__result-tab ${
                            resultTab === 'json' ? 'AICreationUpload__result-tab--active' : ''
                          }`}
                          onClick={() => setResultTab('json')}
                        >
                          JSON
                        </button>
                      </div>

                      {resultTab === 'formatted' ? (
                        <div className='AICreationUpload__result-formatted'>
                          {renderFormattedValue(job.result)}
                        </div>
                      ) : (
                        <pre className='AICreationUpload__result-json'>
                          {JSON.stringify(job.result, null, 2)}
                        </pre>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>

      {isUploadModalOpen && (
        <Modal
          title={t('aiCreationUpload.modalTitle')}
          isOpen={isUploadModalOpen}
          onClose={(isOpen: boolean) => setIsUploadModalOpen(isOpen)}
          classNameContent='Modal__content--creations-form-upload'
          footer={
            <Button
              theme={ButtonTheme.BLUE_OUTLINE}
              onClick={() => setIsUploadModalOpen(false)}
            >
              {t('common.cancel')}
            </Button>
          }
        >
          {DropHeader}
          <ModalUpload onDrop={handleUpload} />
        </Modal>
      )}
    </div>
  )
}

export default AICreationUpload
